import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import type { AccessResource } from '@/constants'

// Route meta: `modules` = Keitaro access_data resources, any of which opens the page; `admin` =
// ADMIN only. The API enforces the same rules (internal/auth/rbac.go, /api/ui admin() routes); the
// guard below only keeps a USER from landing on a page that would answer 403 everywhere.
declare module 'vue-router' {
  interface RouteMeta {
    public?: boolean
    admin?: boolean
    modules?: AccessResource[]
    /** The page needs write access to (any of) `modules`: the file editor shows page source,
     * which a read-only user must never see (the API answers 403 to its calls as well). */
    write?: boolean
  }
}

const shellChildren: RouteRecordRaw[] = [
  {
    path: 'dashboard',
    name: 'dashboard',
    component: () => import('@/pages/DashboardPage.vue'),
    meta: { modules: ['dashboard'] },
  },
  {
    path: 'campaigns',
    name: 'campaigns',
    component: () => import('@/pages/CampaignsPage.vue'),
    meta: { modules: ['campaigns'] },
  },
  {
    path: 'campaigns/:id',
    name: 'campaign-edit',
    component: () => import('@/pages/CampaignEditPage.vue'),
    meta: { modules: ['campaigns'] },
  },
  {
    path: 'landing-pages',
    name: 'landing-pages',
    component: () => import('@/pages/LandingPagesPage.vue'),
    meta: { modules: ['landings'] },
  },
  {
    path: 'offers',
    name: 'offers',
    component: () => import('@/pages/OffersPage.vue'),
    meta: { modules: ['offers'] },
  },
  {
    path: 'pages/:kind/:id/editor',
    name: 'page-editor',
    component: () => import('@/pages/PageEditorPage.vue'),
    meta: { modules: ['landings', 'offers'], write: true },
  },
  {
    path: 'traffic-sources',
    name: 'traffic-sources',
    component: () => import('@/pages/TrafficSourcesPage.vue'),
    meta: { modules: ['traffic_sources'] },
  },
  {
    path: 'click-log',
    name: 'click-log',
    component: () => import('@/pages/ClickLogPage.vue'),
    meta: { modules: ['clicks'] },
  },
  {
    path: 'conversions',
    name: 'conversions',
    component: () => import('@/pages/ConversionsPage.vue'),
    meta: { modules: ['clicks'] },
  },
  {
    path: 'domains',
    name: 'domains',
    component: () => import('@/pages/DomainsPage.vue'),
    meta: { modules: ['domains'] },
  },
  {
    path: 'cro',
    name: 'cro',
    component: () => import('@/pages/CroPage.vue'),
    meta: { modules: ['cro'] },
  },
  {
    path: 'cro/settings',
    name: 'cro-settings',
    component: () => import('@/pages/CroSettingsPage.vue'),
    meta: { admin: true },
  },
  {
    path: 'users',
    name: 'users',
    component: () => import('@/pages/UsersPage.vue'),
    meta: { admin: true },
  },
  {
    path: 'funnel',
    name: 'funnel',
    component: () => import('@/pages/FunnelPage.vue'),
    meta: { admin: true },
  },
  {
    path: 'health',
    name: 'health',
    component: () => import('@/pages/HealthPage.vue'),
    meta: { admin: true },
  },
  {
    path: 'settings',
    name: 'settings',
    component: () => import('@/pages/SettingsPage.vue'),
    meta: { admin: true },
  },
  { path: 'game', name: 'game', component: () => import('@/pages/GamePage.vue') },
]

const router = createRouter({
  history: createWebHistory('/'),
  routes: [
    { path: '/', redirect: '/dashboard' },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/LoginPage.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      component: () => import('@/components/AppShell.vue'),
      children: shellChildren,
    },
    { path: '/:pathMatch(.*)*', redirect: '/dashboard' },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  if (!auth.initialized) await auth.fetchMe()
  if (!to.meta.public && !auth.isAuthenticated) {
    return { path: '/login', query: to.fullPath !== '/' ? { redirect: to.fullPath } : undefined }
  }
  if (to.name === 'login' && auth.isAuthenticated) {
    return { path: auth.homePath() }
  }
  // A USER without the page's resource (or an admin-only page) goes to the first page they may see.
  if (auth.isAuthenticated && (to.meta.admin || to.meta.modules)) {
    if (!auth.canOpen({ admin: to.meta.admin, modules: to.meta.modules })) {
      const home = auth.homePath()
      return home === to.path ? true : { path: home }
    }
    // The editor of a landing/offer: the `kind` param says which module must be writable.
    if (to.meta.write && to.meta.modules) {
      const kind = String(to.params.kind ?? '')
      const module = kind === 'offer' ? 'offers' : kind === 'landing' ? 'landings' : ''
      const writable = module
        ? auth.canWrite(module)
        : to.meta.modules.some((m) => auth.canWrite(m))
      if (!writable) {
        const back = module === 'offers' ? '/offers' : '/landing-pages'
        return { path: back }
      }
    }
  }
  return true
})

export default router
